import { eventIterator, oc } from "@orpc/contract";
import { z } from "zod";
import {
  PricelistEventAction,
  PricelistReceivedAs,
  PricelistVersionSource,
} from "@erp/shared-types";
import { listSortShape } from "../../schemas/common";
import { ExportFileStreamSchema } from "./products.contract";
import { PricelistCaseListFiltersSchema } from "./pricelist-case-filters.contract";
import { PricelistDateSchema } from "./pricelist-primitives.contract";

/**
 * A percentage as a decimal string, optionally negative, up to 4 decimal places
 * — matches the DECIMAL(10,4) column and avoids float drift in transit.
 */
const PercentStringSchema = z
  .string()
  .regex(/^-?\d{1,3}(\.\d{1,4})?$/, "Enter a percentage, e.g. 3.5 or -1.25");

/**
 * The vendor-stated anticipated change, as free text (GEN-2577): vendors quote
 * ranges ("4 to 10%") and several per-group figures for one file, which all
 * have to live in one cell of the price-file log. Trimmed, bounded, otherwise
 * unvalidated — the field's name keeps its `Percent` suffix for the column and
 * audit history it maps to.
 */
export const AnticipatedChangeTextSchema = z.string().trim().max(1000);

// The date primitive lives in `pricelist-primitives.contract` (see there for
// why) and is re-exported so existing imports keep working.
export { PricelistDateSchema };

/**
 * What a table was called in the file it came from — a spreadsheet's tab name.
 *
 * Null for a table nobody named: a PDF's tables, a CSV, anything the model
 * found rather than a sheet. Those are still numbered ("Table 1"), which is
 * all a table with no name of its own can be called.
 */
export const PricelistTableNameSchema = z.string().min(1).max(200).nullable();

export const PricelistTableSchema = z.object({
  headers: z.array(z.string()),
  rows: z.array(z.record(z.string(), z.string())),
  /** Carried through a save so editing a pricelist doesn't rename its tabs. */
  name: PricelistTableNameSchema.optional(),
});

/**
 * Step 1 of the presigned upload flow: reserve a staging key and a URL the
 * browser PUTs the raw bytes to. The file's kind is not knowable yet — the
 * server has no bytes — so the size gate here is the most generous cap of any
 * kind; the per-kind cap is enforced by `uploadPricelist` once the staged
 * blob's header bytes say what it is.
 */
export const GetPricelistUploadUrlInputSchema = z.object({
  fileName: z.string().min(1).max(255),
  /** The browser-reported MIME type; used only as the PUT's Content-Type. */
  contentType: z.string().min(1),
  fileSizeBytes: z.number().int().positive(),
});

export const GetPricelistUploadUrlResponseSchema = z.object({
  /** Opaque staging key, handed back verbatim to `uploadPricelist`. */
  uploadKey: z.string(),
  uploadUrl: z.string().url(),
});

export const UploadPricelistInputSchema = z.object({
  /** The staging key `getUploadUrl` answered with, its blob now uploaded. */
  uploadKey: z.string().min(1),
  fileName: z.string(),
  companyId: z.uuid().optional(),
  /**
   * Attach the new file to this pricelist update case in the SAME transaction
   * that stores it — the case page's own upload. The file takes the case's
   * vendor (a `companyId` that disagrees is refused), the next position, and
   * `caseFileRole` or the role its detected type implies.
   */
  caseId: z.uuid().optional(),
  caseFileRole: z.enum(["notice", "price_file", "supporting"]).optional(),
  /**
   * Compare Files only (GEN-2575): extract the file so it can be compared, but
   * keep it out of Pricelist Management. Cannot be combined with a vendor or
   * a case, which are what put a file in Pricelist Management.
   */
  compareOnly: z.boolean().optional(),
});

/**
 * What an upload can promise once extraction no longer happens on the request.
 *
 * There is deliberately no `csvDocumentId` here any more: the CSV does not exist
 * yet when this answers. Callers that need it read it from the pricelist itself
 * (`get` returns `csvDocuments`), and `backgroundJobId` is how they find out when
 * it is worth looking.
 */
export const UploadPricelistResponseSchema = z.object({
  pdfDocumentId: z.uuid(),
  /**
   * The `core_background_jobs` row tracking the extraction. Null when the
   * file is not extracted at all — an Outlook `.msg` notice (GEN-2521) is
   * stored and attached, and there is no job to wait on.
   */
  backgroundJobId: z.uuid().nullable(),
});

/** The largest pricelist (total rows) that `getExtractedCsv` returns whole. */
export const EXTRACTED_CSV_FULL_READ_MAX_ROWS = 20_000;

/** Row-window size limits for paginated `getExtractedCsv` reads. */
export const EXTRACTED_CSV_WINDOW_MAX_ROWS = 1_000;

export const GetExtractedCsvInputSchema = z.object({
  documentId: z.uuid(),
  /**
   * A row window of one table, for pricelists too large to return whole. When
   * present, only the addressed table carries rows — every table still reports
   * its headers and row count. When absent, the response is complete only if
   * the pricelist is within EXTRACTED_CSV_FULL_READ_MAX_ROWS total rows;
   * otherwise all row arrays are empty and callers page through windows.
   */
  window: z
    .object({
      /** Index into the response's `tables` array. */
      table: z.number().int().nonnegative(),
      offset: z.number().int().nonnegative(),
      limit: z.number().int().positive().max(EXTRACTED_CSV_WINDOW_MAX_ROWS),
    })
    .optional(),
});

export const ExtractedCsvTableSchema = z.object({
  headers: z.array(z.string()),
  /** Total rows in the table, regardless of how many `rows` carries. */
  rowCount: z.number().int().nonnegative(),
  rows: z.array(z.record(z.string(), z.string())),
  /** The source file's name for this table — a sheet's tab, or null. */
  name: PricelistTableNameSchema,
});

export const GetExtractedCsvResponseSchema = z.object({
  tables: z.array(ExtractedCsvTableSchema),
  /**
   * True when every table's `rows` holds all of its rows — the editing
   * surfaces are only safe to enable then, since saves post whole tables.
   */
  complete: z.boolean(),
});

/**
 * One whole table of an extracted pricelist, as a plain CSV download.
 *
 * The compare tool needs a table as a *file*: it parses it client-side the
 * way it parses an uploaded pricelist. The windowed `getExtractedCsv` read is
 * the wrong shape for that — it re-streams the stored blob once per 1,000-row
 * page, and the client would have to reassemble hundreds of megabytes of JSON
 * rows in memory before it could start. This streams the table once, as
 * `text/csv`, bounded on the server by stream backpressure and on the client
 * by whatever the browser does with a Blob.
 */
export const DownloadExtractedTableInputSchema = z.object({
  /** The extracted-CSV document, the same id `getExtractedCsv` takes. */
  documentId: z.uuid(),
  /** Index into the `tables` array `getExtractedCsv` reports for this document. */
  table: z.number().int().nonnegative(),
});

/**
 * Matching rows one search returns before it stops looking. A cap is what makes
 * the search cheap: it lets the server abandon the rest of the blob, which for a
 * common term is most of it. The cost is that totals past the cap are unknown,
 * reported as `truncated` and rendered "200+".
 */
export const EXTRACTED_CSV_SEARCH_MAX_HITS = 200;

/** Shortest query worth scanning a whole pricelist for. */
export const EXTRACTED_CSV_SEARCH_MIN_QUERY = 2;

export const SearchExtractedCsvInputSchema = z.object({
  /** The extracted-CSV document, the same id `getExtractedCsv` takes. */
  documentId: z.uuid(),
  /** Literal text, matched case-insensitively against each cell. */
  query: z.string().min(EXTRACTED_CSV_SEARCH_MIN_QUERY).max(200),
  maxHits: z
    .number()
    .int()
    .positive()
    .max(EXTRACTED_CSV_SEARCH_MAX_HITS)
    .optional(),
});

export const PricelistSearchHitSchema = z.object({
  /**
   * Index into the `tables` array of the same pricelist's `getExtractedCsv`
   * response — tables are ordered by first appearance in both.
   */
  table: z.number().int().nonnegative(),
  /** The row's index within its table, so a window offset can address it. */
  rowIndex: z.number().int().nonnegative(),
  row: z.record(z.string(), z.string()),
});

/**
 * Hits arrive as they are found rather than in one response, so the first match
 * of a cold search reaches the screen long before the scan finishes.
 */
export const PricelistSearchEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("hit"),
    hit: PricelistSearchHitSchema,
  }),
  z.object({
    type: z.literal("done"),
    hitCount: z.number().int().nonnegative(),
    /** True when the hit cap stopped the scan, so there may be more matches. */
    truncated: z.boolean(),
    /** Rows examined, wall-clock ms, and whether the bytes were already resident. */
    rowsScanned: z.number().int().nonnegative(),
    elapsedMs: z.number().nonnegative(),
    cached: z.boolean(),
  }),
]);

export const GetPricelistDocumentUrlInputSchema = z.object({
  documentId: z.uuid(),
});

export const GetPricelistDocumentUrlResponseSchema = z.object({
  url: z.string(),
  fileName: z.string(),
});

export const ListUnassignedPricelistsInputSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

const PricelistCsvDocSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  createdAt: z.date(),
});

const UnassignedPricelistDocSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  createdAt: z.date(),
  uploadedByName: z.string().nullable(),
  /** Recorded on the pricelist's details, YYYY-MM-DD, null when unset. */
  effectiveDate: PricelistDateSchema.nullable(),
  erpUploadDate: PricelistDateSchema.nullable(),
  /**
   * The pricelist update case that owns this file, when one does — the case is
   * then the source of the dates above, not the legacy details row. Null for a
   * pricelist nobody has made a case for, which is every pre-existing one.
   */
  caseId: z.uuid().nullable(),
  /** The owning case's readable number, e.g. `PC-1042`. */
  caseNumber: z.string().nullable(),
  csvDocuments: z.array(PricelistCsvDocSchema),
});

export const ListUnassignedPricelistsResponseSchema = z.object({
  documents: z.array(UnassignedPricelistDocSchema),
  totalCount: z.number(),
});

/**
 * How many cases one `ids`-mode price-file-log export may name.
 *
 * Two ceilings meet here and the lower one wins. 100 is the house cap for a
 * bulk uuid list (`tasks.contract`'s bulk task actions, `memory-bank`'s
 * `ids`, the AR invoice-from-sales-order bulk create). It also has to FIT: the
 * export is a GET the browser navigates to, so the whole input is
 * query-encoded into the URL, and the client refuses a download URL longer
 * than `MAX_DOWNLOAD_URL_LENGTH` (8000 characters). A uuid costs ~45
 * characters once quoted, comma-separated and percent-encoded, so 100 ids is
 * ~4.5KB — inside the budget with room for the cookie header, where the 200
 * that AR's collection-thread bulk actions use (they POST) would not be.
 *
 * Select-all never comes through here: it sends `mode: "filter"`, which is one
 * server-side query regardless of how many cases match.
 */
export const MAX_PRICELIST_LOG_EXPORT_CASE_IDS = 100;

/**
 * Which cases the price-file log exports — a selection, never "everything".
 *
 * A discriminated union rather than `caseIds: string[]` alone because
 * `allSelected` in `PaginatedGentlyTable` means "everything matching the
 * active filter", NOT the ids on the visible page. Modelling select-all as an
 * id list would quietly export 20 rows when the person meant all 400, and a
 * month-end report silently missing rows is worse than one that fails.
 *
 * `filter` carries exactly the vocabulary `pricelistCases.list` filters on
 * today — `companyId` and nothing else — so the export and the list can never
 * disagree about what "everything matching the filter" is. Omitting it means
 * every case in the organization, which is what select-all with no filter
 * means. The server re-applies org scoping and authorization either way and
 * never trusts the filter as sent.
 */
export const ExportPricelistLogInputSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("ids"),
    /** An explicit selection. `.min(1)` — there is no export-everything. */
    caseIds: z.array(z.uuid()).min(1).max(MAX_PRICELIST_LOG_EXPORT_CASE_IDS),
  }),
  /**
   * Select-all: exactly the filters `pricelistCases.list` takes, so "everything
   * matching the active filter" means the same set to the export and the list.
   */
  PricelistCaseListFiltersSchema.extend({ mode: z.literal("filter") }),
]);

/** The upload history's company filter value for "assigned to no company". */
export const PRICELIST_UNASSIGNED_COMPANY_FILTER = "unassigned";

/**
 * The upload history's sortable columns. The dates, the company, the uploader
 * and the case all live off the document row; the server resolves each the way
 * the list displays it, so the order matches what is on screen.
 */
export const PricelistHistorySortBySchema = z.enum([
  "name",
  "companyName",
  "uploadedByName",
  "createdAt",
  "effectiveDate",
  "erpUploadDate",
  "caseNumber",
]);
export type PricelistHistorySortBy = z.infer<
  typeof PricelistHistorySortBySchema
>;

/**
 * The upload history's filters. Every one is optional and they AND together;
 * an empty object is every uploaded pricelist.
 */
export const PricelistHistoryFiltersSchema = z.object({
  /**
   * Case-insensitive substring match across the upload history's text columns:
   * the file name, the company it is assigned to, and the person who uploaded
   * it — plus the price change it is on, by number (`PC-12`, or just `12`) as
   * an exact match, the way the Price Changes list reads it. The date columns
   * are not searched — they are shown in the reader's locale, so what is on
   * screen is not what a match could be made against.
   */
  search: z.string().max(200).optional(),
  /**
   * One company the pricelist is assigned to, or
   * `PRICELIST_UNASSIGNED_COMPANY_FILTER` for those assigned to none.
   */
  companyId: z
    .union([z.uuid(), z.literal(PRICELIST_UNASSIGNED_COMPANY_FILTER)])
    .optional(),
  /** Uploader user ids, OR'd. */
  uploadedBy: z.array(z.uuid()).max(50).optional(),
  /** Uploaded on or after (YYYY-MM-DD, UTC calendar day). */
  uploadedFrom: PricelistDateSchema.optional(),
  /** Uploaded on or before (YYYY-MM-DD, UTC calendar day). */
  uploadedTo: PricelistDateSchema.optional(),
  /** Effective date on or after (YYYY-MM-DD), read as the list shows it. */
  effectiveFrom: PricelistDateSchema.optional(),
  /** Effective date on or before (YYYY-MM-DD). */
  effectiveTo: PricelistDateSchema.optional(),
  /** AS/400 upload date on or after (YYYY-MM-DD), read as the list shows it. */
  erpUploadFrom: PricelistDateSchema.optional(),
  /** AS/400 upload date on or before (YYYY-MM-DD). */
  erpUploadTo: PricelistDateSchema.optional(),
  /** True: only files on a price change. False: only files on none. */
  onCase: z.boolean().optional(),
});
export type PricelistHistoryFilters = z.infer<
  typeof PricelistHistoryFiltersSchema
>;

export const ListAllPricelistsInputSchema =
  PricelistHistoryFiltersSchema.extend({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    /** Absent: newest upload first. */
    ...listSortShape(PricelistHistorySortBySchema),
  });

const AllPricelistDocSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  createdAt: z.date(),
  companyId: z.uuid().nullable(),
  companyName: z.string().nullable(),
  uploadedByName: z.string().nullable(),
  /** Recorded on the pricelist's details, YYYY-MM-DD, null when unset. */
  effectiveDate: PricelistDateSchema.nullable(),
  erpUploadDate: PricelistDateSchema.nullable(),
  /**
   * The pricelist update case that owns this file, when one does — the case is
   * then the source of the dates above, not the legacy details row. Null for a
   * pricelist nobody has made a case for, which is every pre-existing one.
   */
  caseId: z.uuid().nullable(),
  /** The owning case's readable number, e.g. `PC-1042`. */
  caseNumber: z.string().nullable(),
  csvDocuments: z.array(PricelistCsvDocSchema),
});

export const ListAllPricelistsResponseSchema = z.object({
  documents: z.array(AllPricelistDocSchema),
  totalCount: z.number(),
});

export const GetPricelistInputSchema = z.object({
  pdfDocumentId: z.uuid(),
});

// The AS/400 branch map (AS400_BRANCHES_BY_DIVISION / as400BranchesForDivision)
// lives with the rest of the AS/400 division and record-key helpers in
// products.contract.ts; the branch percentages below are read against it.

/**
 * The `percent` of a division the IM team reviewed and marked as not affected
 * by the price change (GEN-2576). Distinct from no entry at all, which means
 * the division hasn't been reviewed yet: the log exports the first as "N/A"
 * and the second blank.
 */
export const DIVISION_PERCENT_NOT_APPLICABLE = "N/A";

/**
 * One division's recorded price change. Divisions come from the org's
 * DataSources, so the set is dynamic; `divisionCode` snapshots the DataSource's
 * title at save time. Divisions that price per branch (GEN-2325) store one
 * entry per branch, distinguished by `branchCode`; an entry without a
 * `branchCode` is the division-wide value.
 */
export const DivisionPriceChangeSchema = z.object({
  dataSourceId: z.uuid(),
  divisionCode: z.string().min(1),
  branchCode: z.string().min(1).optional(),
  percent: z.union([
    PercentStringSchema,
    z.literal(DIVISION_PERCENT_NOT_APPLICABLE),
  ]),
});

/** What the vendor sent: an advance notice, the price file itself, or both. */
export const PricelistReceivedAsSchema = z.nativeEnum(PricelistReceivedAs);

/** The manually maintained pricelist fields. */
export const PricelistDetailsSchema = z.object({
  divisionPriceChanges: z.array(DivisionPriceChangeSchema),
  notes: z.string().nullable(),
  fromTariffChange: z.boolean(),
  anticipatedVendorChangePercent: AnticipatedChangeTextSchema.nullable(),
  /** When the new prices take effect. */
  effectiveDate: PricelistDateSchema.nullable(),
  /** When the pricelist was loaded into the ERP. */
  erpUploadDate: PricelistDateSchema.nullable(),
  /** What the vendor sent (notice / file / both). */
  receivedAs: PricelistReceivedAsSchema.nullable(),
  /** When the change arrived from the vendor. */
  receivedDate: PricelistDateSchema.nullable(),
  /** When the details were last saved; null when never filled in. */
  updatedAt: z.date().nullable(),
});

export const GetPricelistResponseSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  createdAt: z.date(),
  companyId: z.uuid().nullable(),
  companyName: z.string().nullable(),
  /**
   * The assigned company's code (`originId`), the same value `listCompanies`
   * shows as Company Code. Null when unassigned or the company has none.
   */
  originId: z.string().nullable(),
  /**
   * True when the pricelist's company has a ship-and-debit entity module config
   * row — the same check `listCompanies` tags with. False when unassigned.
   */
  shipAndDebitEnabled: z.boolean(),
  /**
   * 5-digit vendor numbers for the assigned company (multiple allowed),
   * joined the same way as `listCompanies` (S&D config, VENDMAST /
   * as400_rexel, and the row sharing this company's code). Empty when
   * unassigned or none are configured.
   */
  vendorNumbers: z.array(z.string()),
  uploadedByName: z.string().nullable(),
  csvDocuments: z.array(PricelistCsvDocSchema),
  details: PricelistDetailsSchema,
  /**
   * The pricelist update case this file is part of, when one exists — what the
   * file page shows as "Part of case PC-1042", linking to the case that is the
   * front door for the price change as a whole.
   *
   * When it is set, `details` above comes from the CASE rather than from this
   * document's legacy `im_pricelist_details` row: a document in
   * `im_pricelist_case_files` reads its metadata from its case, and that rule
   * lives in `PricelistCaseService.resolvePricelistMetadata`, not here.
   */
  case: z
    .object({
      caseId: z.uuid(),
      caseNumber: z.string().nullable(),
      filesReadyAt: PricelistDateSchema.nullable(),
    })
    .nullable(),
});

export const UpdatePricelistDetailsInputSchema = z.object({
  pdfDocumentId: z.uuid(),
  divisionPriceChanges: z.array(DivisionPriceChangeSchema),
  notes: z.string().nullable(),
  fromTariffChange: z.boolean(),
  anticipatedVendorChangePercent: AnticipatedChangeTextSchema.nullable(),
  effectiveDate: PricelistDateSchema.nullable(),
  erpUploadDate: PricelistDateSchema.nullable(),
  receivedAs: PricelistReceivedAsSchema.nullable(),
  receivedDate: PricelistDateSchema.nullable(),
});

export const UpdatePricelistDetailsResponseSchema = z.object({
  details: PricelistDetailsSchema,
});

export const ListPricelistEventsInputSchema = z.object({
  pdfDocumentId: z.uuid(),
});

export const PricelistEventSchema = z.object({
  id: z.uuid(),
  action: z.nativeEnum(PricelistEventAction),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  actorUserId: z.uuid().nullable(),
  /** Display name of the acting user; null for system actions or deleted users. */
  actorName: z.string().nullable(),
  createdAt: z.date(),
});

export const ListPricelistEventsResponseSchema = z.object({
  events: z.array(PricelistEventSchema),
});

export const ListPricelistVersionsInputSchema = z.object({
  pdfDocumentId: z.uuid(),
});

export const PricelistVersionSchema = z.object({
  id: z.uuid(),
  /** 1-based, per pricelist. */
  versionNumber: z.number().int().positive(),
  tableCount: z.number().int().nonnegative(),
  rowCount: z.number().int().nonnegative(),
  source: z.nativeEnum(PricelistVersionSource),
  /** Whether this version is the pricelist's live contents. */
  isCurrent: z.boolean(),
  /**
   * True when the version's CSV text exceeded the inline-snapshot limit and
   * only counts were kept. Such a version cannot be restored.
   */
  contentOmitted: z.boolean(),
  /** Display name of the version's author; null for system or deleted users. */
  createdByName: z.string().nullable(),
  createdAt: z.date(),
});

export const ListPricelistVersionsResponseSchema = z.object({
  versions: z.array(PricelistVersionSchema),
});

export const RestorePricelistVersionInputSchema = z.object({
  pdfDocumentId: z.uuid(),
  versionId: z.uuid(),
});

export const RestorePricelistVersionResponseSchema = z.object({
  versionId: z.uuid(),
  versionNumber: z.number().int().positive(),
});

export const UpdateExtractedCsvInputSchema = z.object({
  csvDocumentId: z.uuid(),
  /** The full replacement contents — every table, in tab order. */
  tables: z.array(PricelistTableSchema).min(1),
});

export const UpdateExtractedCsvResponseSchema = z.object({
  csvDocumentId: z.uuid(),
});

export const RetryExtractionInputSchema = z.object({
  pdfDocumentId: z.uuid(),
});

export const RetryExtractionResponseSchema = z.object({
  /**
   * The current extracted CSV, if one exists; otherwise the source document
   * id. Re-extract no longer waits on the request (GEN-2394), so this is the
   * CSV that will be replaced — not a freshly extracted one. Poll
   * `backgroundJobId`, then re-read the pricelist.
   */
  csvDocumentId: z.uuid(),
  /** The `core_background_jobs` row tracking the re-extraction. */
  backgroundJobId: z.uuid(),
});

export const AssignPricelistToCompanyInputSchema = z.object({
  pdfDocumentId: z.uuid(),
  companyId: z.uuid(),
});

export const AssignPricelistToCompanyResponseSchema = z.object({
  companyId: z.uuid(),
  companyName: z.string(),
});

export const DeletePricelistInputSchema = z.object({
  pdfDocumentId: z.uuid(),
});

export const DeletePricelistResponseSchema = z.object({
  success: z.boolean(),
});

export const pricelistContracts = {
  getUploadUrl: oc
    .input(GetPricelistUploadUrlInputSchema)
    .output(GetPricelistUploadUrlResponseSchema)
    .route({
      method: "POST",
      summary: "Get an upload URL for a pricelist",
      description:
        "Reserves a short-lived signed staging URL the browser uploads a pricelist file to, plus the key to register it with afterwards.",
    }),
  uploadPricelist: oc
    .input(UploadPricelistInputSchema)
    .output(UploadPricelistResponseSchema)
    .route({
      method: "POST",
      summary: "Upload a pricelist file",
      description:
        "Registers an uploaded vendor pricelist file, optionally for a company or update case, and starts background extraction of its price tables (email notices are stored only).",
    }),
  getExtractedCsv: oc
    .input(GetExtractedCsvInputSchema)
    .output(GetExtractedCsvResponseSchema)
    .route({
      method: "GET",
      summary: "Get a pricelist's extracted price tables",
      description:
        "Returns the tables extracted from a pricelist with headers and row counts; large pricelists are read one row window of one table at a time.",
    }),
  // GET like every other streaming download here: the response is a file the
  // client fetches and wraps in a File, not a JSON document.
  downloadExtractedTable: oc
    .input(DownloadExtractedTableInputSchema)
    .output(ExportFileStreamSchema)
    .route({
      method: "GET",
      summary: "Download an extracted pricelist table as CSV",
      description:
        "Streams one table extracted from a pricelist as a plain CSV file, regardless of size.",
    }),
  searchExtractedCsv: oc
    .input(SearchExtractedCsvInputSchema)
    .output(eventIterator(PricelistSearchEventSchema))
    // POST, not GET: the query belongs in a body, and the response is a stream
    // rather than a cacheable document.
    .route({
      method: "POST",
      summary: "Search a pricelist's extracted tables",
      description:
        "Streams cells in one pricelist's extracted tables that contain the given text, case-insensitive, up to a hit limit.",
    }),
  getDocumentUrl: oc
    .input(GetPricelistDocumentUrlInputSchema)
    .output(GetPricelistDocumentUrlResponseSchema)
    .route({
      method: "GET",
      summary: "Get a pricelist document download link",
      description:
        "Returns a short-lived signed URL to view or download a stored pricelist document.",
    }),
  listUnassigned: oc
    .input(ListUnassignedPricelistsInputSchema)
    .output(ListUnassignedPricelistsResponseSchema)
    .route({
      method: "GET",
      summary: "List pricelists not assigned to a company",
      description:
        "Paged list of uploaded pricelist files with no company assignment yet, with their extracted CSV documents.",
    }),
  // A bulk action over the case list's selection, not a report button: GET so
  // the browser can navigate to it and stream the workbook straight to disk
  // (the house pattern for every other streaming export — `exportCatalog`,
  // `exportAs400View`, `productCatalog.exportExcel`, `as400Publish.download`
  // are all GET with query-encoded input). The selection union rides in the
  // RPC `?data=` payload, which is why the id cap has to fit a URL.
  exportLog: oc
    .input(ExportPricelistLogInputSchema)
    .output(ExportFileStreamSchema)
    .route({
      method: "GET",
      summary: "Export pricelist update cases as a price log",
      description:
        "Streams an Excel price-file log for the selected pricelist update cases, one row per case.",
    }),
  listAll: oc
    .input(ListAllPricelistsInputSchema)
    .output(ListAllPricelistsResponseSchema)
    .route({
      method: "GET",
      summary: "List uploaded pricelists",
      description:
        "Paged upload history of all pricelist files, newest first unless sorted, searchable by file name, assigned company, uploader or price change number, and filterable by company, uploader, the three dates and whether a file is on a price change.",
    }),
  get: oc
    .input(GetPricelistInputSchema)
    .output(GetPricelistResponseSchema)
    .route({
      method: "GET",
      summary: "Get a pricelist with its details",
      description:
        "Returns one pricelist source document with its company assignment, extracted CSV and maintained details such as effective date, division price changes and notes.",
    }),
  updateDetails: oc
    .input(UpdatePricelistDetailsInputSchema)
    .output(UpdatePricelistDetailsResponseSchema)
    .route({
      method: "PUT",
      summary: "Update a pricelist's details",
      description:
        "Replaces a pricelist's maintained details: division price changes, tariff flag, anticipated change, dates, received-as and notes. Refused when the pricelist belongs to an update case.",
    }),
  listEvents: oc
    .input(ListPricelistEventsInputSchema)
    .output(ListPricelistEventsResponseSchema)
    .route({
      method: "GET",
      summary: "List a pricelist's history events",
      description:
        "Returns a pricelist's history events (edits, restores, assignments, deletions), newest first.",
    }),
  listVersions: oc
    .input(ListPricelistVersionsInputSchema)
    .output(ListPricelistVersionsResponseSchema)
    .route({
      method: "GET",
      summary: "List a pricelist's saved versions",
      description:
        "Returns the saved versions of a pricelist's extracted tables, newest first.",
    }),
  restoreVersion: oc
    .input(RestorePricelistVersionInputSchema)
    .output(RestorePricelistVersionResponseSchema)
    .route({
      method: "POST",
      summary: "Restore a previous pricelist version",
      description:
        "Restores a pricelist's extracted tables to an earlier saved version, makes it current and records a history event.",
    }),
  updateExtractedCsv: oc
    .input(UpdateExtractedCsvInputSchema)
    .output(UpdateExtractedCsvResponseSchema)
    .route({
      method: "POST",
      summary: "Save edits to a pricelist's extracted tables",
      description:
        "Replaces a pricelist's extracted tables with edited contents, saving a new version and recording a history event.",
    }),
  retryExtraction: oc
    .input(RetryExtractionInputSchema)
    .output(RetryExtractionResponseSchema)
    .route({
      method: "POST",
      summary: "Re-run extraction on a pricelist",
      description:
        "Starts a background re-extraction of a pricelist file, replacing or creating its extracted CSV document; returns the background job.",
    }),
  assignToCompany: oc
    .input(AssignPricelistToCompanyInputSchema)
    .output(AssignPricelistToCompanyResponseSchema)
    .route({
      method: "POST",
      summary: "Assign a pricelist to a company",
      description:
        "Assigns or reassigns a pricelist file to a company, replacing any existing assignment, and records a history event.",
    }),
  delete: oc
    .input(DeletePricelistInputSchema)
    .output(DeletePricelistResponseSchema)
    .route({
      method: "DELETE",
      summary: "Delete a pricelist",
      description:
        "Soft-deletes a pricelist so it disappears from all pricelist views while staying recoverable; records a history event.",
    }),
} as const;

export type PricelistTable = z.infer<typeof PricelistTableSchema>;
export type ExtractedCsvTable = z.infer<typeof ExtractedCsvTableSchema>;
export type GetExtractedCsvResponse = z.infer<
  typeof GetExtractedCsvResponseSchema
>;
export type GetPricelistUploadUrlInput = z.infer<
  typeof GetPricelistUploadUrlInputSchema
>;
export type GetPricelistUploadUrlResponse = z.infer<
  typeof GetPricelistUploadUrlResponseSchema
>;
export type UploadPricelistInput = z.infer<typeof UploadPricelistInputSchema>;
export type UploadPricelistResponse = z.infer<
  typeof UploadPricelistResponseSchema
>;
export type GetExtractedCsvInput = z.infer<typeof GetExtractedCsvInputSchema>;
export type DownloadExtractedTableInput = z.infer<
  typeof DownloadExtractedTableInputSchema
>;
export type SearchExtractedCsvInput = z.infer<
  typeof SearchExtractedCsvInputSchema
>;
export type PricelistSearchHit = z.infer<typeof PricelistSearchHitSchema>;
export type PricelistSearchEvent = z.infer<typeof PricelistSearchEventSchema>;
export type GetPricelistDocumentUrlInput = z.infer<
  typeof GetPricelistDocumentUrlInputSchema
>;
export type GetPricelistDocumentUrlResponse = z.infer<
  typeof GetPricelistDocumentUrlResponseSchema
>;
export type ListAllPricelistsResponse = z.infer<
  typeof ListAllPricelistsResponseSchema
>;
export type AllPricelistDoc = ListAllPricelistsResponse["documents"][number];
export type ExportPricelistLogInput = z.infer<
  typeof ExportPricelistLogInputSchema
>;
export type GetPricelistInput = z.infer<typeof GetPricelistInputSchema>;
export type GetPricelistResponse = z.infer<typeof GetPricelistResponseSchema>;
export type UpdateExtractedCsvInput = z.infer<
  typeof UpdateExtractedCsvInputSchema
>;
export type DivisionPriceChange = z.infer<typeof DivisionPriceChangeSchema>;
export type PricelistDetails = z.infer<typeof PricelistDetailsSchema>;
export type UpdatePricelistDetailsInput = z.infer<
  typeof UpdatePricelistDetailsInputSchema
>;
export type PricelistEventDTO = z.infer<typeof PricelistEventSchema>;
export type ListPricelistEventsResponse = z.infer<
  typeof ListPricelistEventsResponseSchema
>;
export type PricelistVersionDTO = z.infer<typeof PricelistVersionSchema>;
export type ListPricelistVersionsResponse = z.infer<
  typeof ListPricelistVersionsResponseSchema
>;
export type RestorePricelistVersionInput = z.infer<
  typeof RestorePricelistVersionInputSchema
>;
export type AssignPricelistToCompanyInput = z.infer<
  typeof AssignPricelistToCompanyInputSchema
>;
export type AssignPricelistToCompanyResponse = z.infer<
  typeof AssignPricelistToCompanyResponseSchema
>;
export type DeletePricelistInput = z.infer<typeof DeletePricelistInputSchema>;
export type DeletePricelistResponse = z.infer<
  typeof DeletePricelistResponseSchema
>;
